import { useDisclosure } from '@mantine/hooks'
import {
  alpha,
  Box,
  Divider,
  Drawer,
  List,
  Stack,
  useTheme,
} from '@mui/material'
import { nanoid } from 'nanoid'
import { useMemo } from 'react'
import { useLocation } from 'react-router-dom'

import GuidanceDrawer from '~/features/onboarding/components/guidanceDrawer'
import useGetElementDimensions from '~/features/ui/hooks/useGetElementDimensions'
import EElement from '~/features/ui/models'
import {
  SIDE_NAV_HEADER_CLASS_NAME,
  SIDE_NAV_WIDTH,
  SIDE_NAV_WIDTH_COLLAPSED,
} from '~/layouts/account/header/nav/side/constants'
import GuidanceItem from '~/layouts/account/header/nav/side/guidanceItem'
import NavItem from '~/layouts/account/header/nav/side/navItem'
import useNavItems from '~/layouts/account/hooks/useNavItems'
import { EAdminPortalClientPath, EClientPath } from '~/models/api'
import rem from '~/utils/ui'

type TProps = {
  isOpen: boolean
  close: () => void
  isCollapsed?: boolean
}

function SideNav({ isOpen, close, isCollapsed = false }: TProps) {
  const theme = useTheme()
  const { height: headerHeight } = useGetElementDimensions(
    EElement.accountHeader,
  )
  const [isGuidanceOpen, { open: openGuidance, close: closeGuidance }] =
    useDisclosure()
  const navItems = useNavItems()
  const width = isCollapsed ? SIDE_NAV_WIDTH_COLLAPSED : SIDE_NAV_WIDTH

  const location = useLocation()
  const isNotAllowedToSeeGuidance = useMemo(
    () =>
      location.pathname.includes(EAdminPortalClientPath.admin) ||
      location.pathname.includes(EClientPath.partnerAdmin),
    [location],
  )

  return (
    <Drawer
      variant="permanent"
      id={EElement.accountSideNav}
      sx={{
        display: { xxxs: isOpen ? 'block' : 'none', md: 'block' },
        width: { xxxs: '100%', md: rem(width) },
        '&:hover': {
          width: { md: rem(SIDE_NAV_WIDTH) },
        },
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          boxSizing: 'border-box',
          border: 'none',
          backgroundColor: theme.palette.menuBackground,
          zIndex: theme.zIndex.drawer,
          width: { xxxs: '100%', md: rem(SIDE_NAV_WIDTH) },
          overflowX: 'hidden',
          transition: 'width 0.3s ease-out',
          '& .MuiListItemButton-root': {
            minWidth: { md: 'max-content' },
            '& *': {
              transition: 'opacity 0.3s ease-out',
            },
          },
          '& .MuiCollapse-root': {
            overflow: 'hidden',
            transition: 'max-height 0.3s ease-out',
            maxHeight: { md: rem(1200) },
          },
          [`& .${SIDE_NAV_HEADER_CLASS_NAME}`]: {
            transition: 'all 0.3s ease-out',
          },
          '&:not(:hover)': (isCollapsed || undefined) && {
            width: { md: rem(SIDE_NAV_WIDTH_COLLAPSED) },
            '& .MuiListItemButton-root *:not(:first-of-type)': {
              opacity: { md: 0 },
            },
            '& .MuiCollapse-root': {
              maxHeight: { md: 0 },
            },
            [`& .${SIDE_NAV_HEADER_CLASS_NAME}`]: {
              py: 0,
              opacity: 0,
              overflow: 'hidden',
            },
          },
        },
      }}
    >
      <Stack
        spacing={2}
        sx={{
          pt: rem(13),
          pb: 0,
          mt: rem(headerHeight),
          alignItems: 'center',
          height: '100%',
          pl: isCollapsed ? { xxxs: rem(23), md: rem(16) } : rem(23),
          transition: 'padding 0.3s ease-out',
          '&:hover': {
            pl: rem(23),
          },
        }}
      >
        <List
          component="nav"
          sx={{
            width: '100%',
            height: '100%',
            py: 0,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box
            sx={{
              flex: '1 1 auto',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: { xxxs: 'flex-start', md: 'space-between' },
            }}
          >
            <Box
              sx={{
                flex: '1 1 auto',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: { xxxs: 'flex-start', md: 'space-between' },
              }}
            >
              <Box>
                {navItems
                  .filter((navItem) => !navItem.isOnBottom)
                  .map((navItem) => (
                    <NavItem key={nanoid()} navItem={navItem} close={close} />
                  ))}
              </Box>
              <Box>
                <Divider
                  sx={{
                    backgroundColor: alpha(theme.palette.menuFont, 0.2),
                    marginLeft: rem(-23),
                    marginY: rem(12),
                  }}
                />
                {isNotAllowedToSeeGuidance ? null : (
                  <GuidanceItem onOpen={openGuidance} />
                )}
                {navItems
                  .filter((navItem) => navItem.isOnBottom)
                  .map((navItem) => (
                    <NavItem key={nanoid()} navItem={navItem} close={close} />
                  ))}
              </Box>
            </Box>
          </Box>
        </List>
      </Stack>
      {isNotAllowedToSeeGuidance ? null : (
        <GuidanceDrawer
          open={isGuidanceOpen}
          onClose={closeGuidance}
          onMenuClose={close}
        />
      )}
    </Drawer>
  )
}

export default SideNav
